import {
  getTimelineRowHeight,
  getTimelineRowSlotHeight,
  VIDEO_EDITOR_TIMELINE_MAX_VISIBLE_ROWS,
  VIDEO_EDITOR_TIMELINE_SECTION_PADDING_BOTTOM,
  VIDEO_EDITOR_TIMELINE_SECTION_PADDING_TOP,
  VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT,
  VIDEO_EDITOR_TRACK_TYPE,
} from "./constants";
import { getImageTracksOrdered, getAudioTracksOrdered, getVideoTracksOrdered } from "./clipTimelineUtils";
import { getTrackByType } from "./projectSchema";
import { areEditorSubtitlesEnabled } from "./subtitleEdl";

export const getTimelineRowsLayout = ({ edl }) => {
  const imageTracks = getImageTracksOrdered({ edl });
  const rows = [];
  let top = VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT;

  imageTracks.forEach((track) => {
    rows.push({
      id: track.id,
      type: VIDEO_EDITOR_TRACK_TYPE.IMAGE,
      trackId: track.id,
      top,
      height: getTimelineRowHeight({ type: VIDEO_EDITOR_TRACK_TYPE.IMAGE }),
      slotHeight: getTimelineRowSlotHeight({
        type: VIDEO_EDITOR_TRACK_TYPE.IMAGE,
      }),
      isContentRow: false,
    });
    top += getTimelineRowSlotHeight({ type: VIDEO_EDITOR_TRACK_TYPE.IMAGE });
  });

  const subtitleTrack = getTrackByType({
    edl,
    type: VIDEO_EDITOR_TRACK_TYPE.SUBTITLE,
  });
  if (subtitleTrack && areEditorSubtitlesEnabled(edl?.subtitleOptions)) {
    rows.push({
      id: subtitleTrack.id,
      type: VIDEO_EDITOR_TRACK_TYPE.SUBTITLE,
      trackId: subtitleTrack.id,
      top,
      height: getTimelineRowHeight({ type: VIDEO_EDITOR_TRACK_TYPE.SUBTITLE }),
      slotHeight: getTimelineRowSlotHeight({
        type: VIDEO_EDITOR_TRACK_TYPE.SUBTITLE,
      }),
      isContentRow: false,
    });
    top += getTimelineRowSlotHeight({
      type: VIDEO_EDITOR_TRACK_TYPE.SUBTITLE,
    });
  }

  const videoTracks = getVideoTracksOrdered({ edl });
  const audioTracks = getAudioTracksOrdered({ edl });
  videoTracks.forEach((track, index) => {
    const isLastVideo = index === videoTracks.length - 1 && audioTracks.length === 0;
    rows.push({
      id: track.id,
      type: VIDEO_EDITOR_TRACK_TYPE.VIDEO,
      trackId: track.id,
      top,
      height: getTimelineRowHeight({ type: VIDEO_EDITOR_TRACK_TYPE.VIDEO }),
      slotHeight: getTimelineRowSlotHeight({
        type: VIDEO_EDITOR_TRACK_TYPE.VIDEO,
        isLast: isLastVideo,
      }),
      isContentRow: true,
    });
    top += getTimelineRowSlotHeight({
      type: VIDEO_EDITOR_TRACK_TYPE.VIDEO,
      isLast: isLastVideo,
    });
  });

  audioTracks.forEach((track, index) => {
    const isLast = index === audioTracks.length - 1;
    rows.push({
      id: track.id,
      type: VIDEO_EDITOR_TRACK_TYPE.AUDIO,
      trackId: track.id,
      top,
      height: getTimelineRowHeight({ type: VIDEO_EDITOR_TRACK_TYPE.AUDIO }),
      slotHeight: getTimelineRowSlotHeight({
        type: VIDEO_EDITOR_TRACK_TYPE.AUDIO,
        isLast,
      }),
      isContentRow: false,
    });
    top += getTimelineRowSlotHeight({
      type: VIDEO_EDITOR_TRACK_TYPE.AUDIO,
      isLast,
    });
  });

  return {
    rows,
    contentHeight: top - VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT,
    totalHeight: top,
  };
};

const getTimelineShellChromeHeight = () =>
  VIDEO_EDITOR_TIMELINE_SECTION_PADDING_TOP +
  VIDEO_EDITOR_TIMELINE_SECTION_PADDING_BOTTOM +
  VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT;

export const getTimelineShellContentHeight = ({ edl }) => {
  const chromeHeight = getTimelineShellChromeHeight();
  if (!edl) {
    return chromeHeight;
  }

  const { contentHeight } = getTimelineRowsLayout({ edl });
  return chromeHeight + contentHeight;
};

export const getTimelineShellMinHeight = ({ edl }) => {
  const chromeHeight = getTimelineShellChromeHeight();

  if (!edl) {
    return chromeHeight;
  }

  const { rows } = getTimelineRowsLayout({ edl });
  const visibleRows = rows.slice(0, VIDEO_EDITOR_TIMELINE_MAX_VISIBLE_ROWS);
  const visibleRowsHeight = visibleRows.reduce((total, row, index) => {
    const isLastVisible = index === visibleRows.length - 1;
    const isLastOverall = row === rows[rows.length - 1];
    if (isLastVisible && isLastOverall) {
      return total + row.height;
    }
    return total + row.slotHeight;
  }, 0);

  return chromeHeight + visibleRowsHeight;
};

export const getTimelineRowTop = ({ edl, type, trackId }) => {
  const { rows } = getTimelineRowsLayout({ edl });

  if (type === VIDEO_EDITOR_TRACK_TYPE.IMAGE && trackId) {
    const imageRow = rows.find((row) => row.trackId === trackId);
    return imageRow?.top ?? VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT;
  }

  if (type === VIDEO_EDITOR_TRACK_TYPE.IMAGE) {
    const imageRows = rows.filter(
      (row) => row.type === VIDEO_EDITOR_TRACK_TYPE.IMAGE
    );
    const bottomImageRow = imageRows[imageRows.length - 1];
    return bottomImageRow?.top ?? VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT;
  }

  if (type === VIDEO_EDITOR_TRACK_TYPE.AUDIO && trackId) {
    const audioRow = rows.find((row) => row.trackId === trackId);
    return audioRow?.top ?? rows.find((item) => item.type === type)?.top;
  }

  if (type === VIDEO_EDITOR_TRACK_TYPE.AUDIO) {
    const audioRows = rows.filter(
      (row) => row.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO
    );
    const bottomAudioRow = audioRows[audioRows.length - 1];
    return bottomAudioRow?.top ?? rows.find((item) => item.type === type)?.top;
  }

  if (type === VIDEO_EDITOR_TRACK_TYPE.VIDEO && trackId) {
    const videoRow = rows.find((row) => row.trackId === trackId);
    return videoRow?.top ?? rows.find((item) => item.type === type)?.top;
  }

  if (type === VIDEO_EDITOR_TRACK_TYPE.VIDEO) {
    const videoRows = rows.filter(
      (row) => row.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO
    );
    const bottomVideoRow = videoRows[videoRows.length - 1];
    return bottomVideoRow?.top ?? rows.find((item) => item.type === type)?.top;
  }

  const row = rows.find((item) => item.type === type);
  return row?.top ?? VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT;
};

export const resolveTimelineRowAtOffset = ({ edl, offsetY }) => {
  const { rows } = getTimelineRowsLayout({ edl });
  const relativeY = Math.max(0, offsetY);

  return (
    rows.find(
      (row) => relativeY >= row.top && relativeY < row.top + row.slotHeight
    ) || null
  );
};

export const getTimelineRowScrollOffset = (timelineAreaNode) => {
  const grid = timelineAreaNode?.querySelector?.(
    ".timeline-editor-edit-area .ReactVirtualized__Grid"
  );
  const editArea = timelineAreaNode?.querySelector?.(
    ".timeline-editor-edit-area"
  );
  return Math.max(grid?.scrollTop || 0, editArea?.scrollTop || 0);
};
